import {
  Check,
  CheckCircle2,
  Circle,
  FileSignature,
  Image as ImageIcon,
  RotateCcw,
  Send,
  X,
  XCircle
} from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FirmSignaturesAPI } from "@/features/clientPortal/api";
import {
  refreshClientPortal,
  useFirmSignatures
} from "@/features/clientPortal/queries";
import type {
  FirmSignatureRequest,
  UploadCheck
} from "@/features/clientPortal/types";
import { cn } from "@/lib/utils";

import { SectionCard } from "../../components/SectionCard";
import { useClientProfile } from "../../context/ClientProfileContext";
import {
  kindLabel,
  needsReview,
  statusLine,
  visibleRequests
} from "../../signing/signingModel";

const openDocument = async (clientId: string, documentId: string) => {
  const tab = window.open("", "_blank");
  const blob = await ForeignNationalDocumentsAPI.getFileBlob(
    clientId,
    documentId
  );
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.assign(url);
};

const CheckLine: FC<{ ok: boolean | null; yes: string; no: string }> = ({
  ok,
  yes,
  no
}) => {
  const Icon = ok === null ? Circle : ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-center gap-1.5">
      <Icon
        className={cn(
          "size-3.5",
          ok ? "text-success" : ok === false ? "text-warn" : ""
        )}
      />
      {ok === false ? no : yes}
    </li>
  );
};

const PhotoCheck: FC<{ check: UploadCheck }> = ({ check }) =>
  check.checked ? (
    <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
      <CheckLine
        ok={check.signature_found}
        yes="Signature found in the box"
        no="No signature seen in the box"
      />
      <CheckLine
        ok={check.readable && check.whole_page}
        yes="Whole page visible and readable"
        no="Page cut off or hard to read"
      />
      {check.problems.map((p) => (
        <li key={p} className="ps-5 text-warn">
          {p}
        </li>
      ))}
    </ul>
  ) : (
    <span className="text-xs text-muted-foreground">
      The photo couldn't be checked automatically.
    </span>
  );

const RequestItem: FC<{
  clientId: string;
  first: string;
  request: FirmSignatureRequest;
  onChanged: () => Promise<unknown>;
}> = ({ clientId, first, request: r, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");

  const run = (work: Promise<unknown>, done: string) => {
    setBusy(true);
    work
      .then(() => {
        toast.success(done);
        return onChanged();
      })
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error ? error.message : "Couldn't save. Try again."
        )
      )
      .finally(() => setBusy(false));
  };
  const open = (documentId: string) =>
    void openDocument(clientId, documentId).catch(() =>
      toast.error("Couldn't open the document.")
    );

  const done = r.status === "signed" || r.status === "completed";
  return (
    <li
      className="flex flex-col gap-2 border-t border-border py-3 first:border-t-0 first:pt-0"
      data-testid="signature-request"
    >
      <div className="flex items-start gap-2">
        <FileSignature
          className={cn(
            "mt-0.5 size-4 shrink-0",
            done ? "text-success" : "text-muted-foreground"
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
            {r.title}
            <Badge variant="outline" className="font-normal">
              {kindLabel(r)}
            </Badge>
            {r.case_label ? (
              <span className="text-xs font-normal text-muted-foreground">
                {r.case_label}
              </span>
            ) : null}
          </span>
          <span
            className={cn(
              "text-xs",
              needsReview(r) ? "text-warn" : "text-muted-foreground"
            )}
          >
            {statusLine(r, first)}
          </span>
          {r.status === "signed" && r.signed ? (
            <span
              className="truncate font-mono text-xs text-muted-foreground"
              title={`Original SHA-256 ${
                r.signed.source_sha256
              }\nSigned SHA-256 ${r.signed.signed_sha256}\nIP ${
                r.signed.ip ?? "unknown"
              }\n${r.signed.user_agent ?? ""}`}
            >
              IP {r.signed.ip ?? "unknown"} · SHA-256{" "}
              {r.signed.signed_sha256.slice(0, 12)}…
            </span>
          ) : null}
        </span>
      </div>

      {needsReview(r) && r.ink ? (
        <div className="ms-6 flex flex-col gap-2 rounded-lg border border-border bg-fill-1 p-2.5">
          <PhotoCheck check={r.ink.check} />
          {returning ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`return-${r.id}`}>
                What should {first} fix? (they see this)
              </Label>
              <Textarea
                id={`return-${r.id}`}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {r.status === "upcoming" ? (
          <Button
            variant="primary"
            size="xs"
            disabled={busy}
            onClick={() =>
              run(
                FirmSignaturesAPI.open(clientId, r.id),
                `Sent. ${first} can sign it now.`
              )
            }
          >
            <Send /> Send now
          </Button>
        ) : null}
        {needsReview(r) && r.ink ? (
          returning ? (
            <>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setReturning(false)}
              >
                Back
              </Button>
              <Button
                size="xs"
                variant="destructive"
                disabled={busy || !note.trim()}
                onClick={() =>
                  run(
                    FirmSignaturesAPI.review(clientId, r.id, {
                      accepted: false,
                      note: note.trim()
                    }),
                    `Sent back to ${first}`
                  )
                }
              >
                <RotateCcw /> Ask again
              </Button>
            </>
          ) : (
            <>
              <Button
                size="xs"
                variant="outline"
                onClick={() => open(r.ink?.document_id ?? "")}
              >
                <ImageIcon /> Open photo
              </Button>
              <Button
                size="xs"
                variant="ghost"
                disabled={busy}
                onClick={() => setReturning(true)}
              >
                Ask again…
              </Button>
              <Button
                variant="primary"
                size="xs"
                disabled={busy}
                onClick={() =>
                  run(
                    FirmSignaturesAPI.review(clientId, r.id, {
                      accepted: true
                    }),
                    "Ink signature accepted"
                  )
                }
                data-testid="ink-accept"
              >
                <Check /> Accept
              </Button>
            </>
          )
        ) : null}
        {done && (r.signed_document_id || r.ink) ? (
          <Button
            size="xs"
            variant="outline"
            onClick={() =>
              open(r.signed_document_id ?? r.ink?.document_id ?? "")
            }
          >
            Open signed copy
          </Button>
        ) : null}
        {r.status === "upcoming" || r.status === "pending" ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              run(FirmSignaturesAPI.cancel(clientId, r.id), "Request cancelled")
            }
          >
            <X /> Cancel
          </Button>
        ) : null}
      </div>
    </li>
  );
};

/** Documents out for the client's signature. Renders nothing when there are none. */
export const SignatureRequestsCard: FC = () => {
  const { clientId, client } = useClientProfile();
  const requests = useFirmSignatures(clientId);
  const shown = visibleRequests(requests.data ?? []);
  if (!shown.length) return null;
  const first = (client?.first_name ?? "").trim() || "The client";
  const review = shown.filter(needsReview).length;
  const onChanged = () =>
    Promise.all([requests.refetch(), refreshClientPortal()]);
  return (
    <SectionCard
      title={
        review
          ? `${first} sent ${
              review === 1 ? "a signed page" : `${review} signed pages`
            } to check`
          : "Out for signature"
      }
    >
      <ul className="flex flex-col">
        {[...shown]
          .sort((a, b) => Number(needsReview(b)) - Number(needsReview(a)))
          .map((r) => (
            <RequestItem
              key={r.id}
              clientId={clientId}
              first={first}
              request={r}
              onChanged={onChanged}
            />
          ))}
      </ul>
    </SectionCard>
  );
};
